// Move to's pure half (#6771, epic #6552; prototype menuHTML `:1901-1917`, the search
// qHit, qEx, qApply and qFirst and its keys `:1800-1847`): the order of the
// machines, the search over them and what Enter picks. `components/machine-card/MoveToMenu.tsx`
// renders it over the server's ONE preview; nothing here decides whether a machine can take
// the work: `canTake` is the preview's own answer (no refusal on that target).

/** The search shows at this many machines or more. */
export const SEARCH_AT = 4;

/**
 * A search is shown at `at` entries or more, AND whenever its query is not empty (#6772 review
 * round 10): a re-read that shrinks the list below `at` must not hide a box whose query still
 * filters the rows, or the work it hides is unreachable.
 */
export const searchVisible = (count: number, at: number, query: string): boolean =>
  count >= at || query !== '';

/** What the search reads of one machine. */
export type MenuMachine = {
  readonly id: string;
  readonly name: string;
  /** The machine's 8-char id, the label's second half (machine labels are name plus id). */
  readonly shortId: string;
  /** The server's label or address, or null when it has neither. */
  readonly server: string | null;
  /** The card's state word ("Autonomous", "Offline", …). */
  readonly stateWord: string;
};

type Ordered = { readonly canTake: boolean; readonly cpuBefore: number | null };

/**
 * Machines that can take the work first, then by lowest current CPU (the prototype's order),
 * a machine whose CPU is not known after every known one. Stable otherwise.
 */
export function orderOptions<O extends Ordered>(opts: readonly O[]): O[] {
  const cpu = (o: O): number => o.cpuBefore ?? Number.POSITIVE_INFINITY;
  return [...opts].sort((a, b) => Number(b.canTake) - Number(a.canTake) || cpu(a) - cpu(b));
}

/** The words the search matches: the name, the 8-char id, the server and the state word. */
export function machineHay(m: MenuMachine): string {
  return [m.name, m.shortId, m.server, m.stateWord].filter((s) => s !== null && s !== '').join(' ');
}

const words = (q: string): string[] => q.trim().toLowerCase().split(/\s+/).filter(Boolean);

/** Every word of `q` is in `hay`, case-insensitive; a blank query matches everything. */
export function queryHits(q: string, hay: string): boolean {
  const h = hay.toLowerCase();
  return words(q).every((w) => h.includes(w));
}

/** The name equals the query, or starts with it followed by a space. */
export function exactName(q: string, name: string): boolean {
  const query = q.trim().toLowerCase();
  if (query === '') return false;
  const n = name.toLowerCase();
  return n === query || n.startsWith(`${query} `);
}

/** The options the query shows, in order, an exact name first. */
export function searchOptions<O extends { readonly machine: MenuMachine }>(
  ordered: readonly O[],
  q: string,
): O[] {
  const shown = ordered.filter((o) => queryHits(q, machineHay(o.machine)));
  return [
    ...shown.filter((o) => exactName(q, o.machine.name)),
    ...shown.filter((o) => !exactName(q, o.machine.name)),
  ];
}

/** What Enter moves to: the first shown machine that can take it, or null. */
export function enterTarget<O extends { readonly canTake: boolean }>(
  shown: readonly O[],
): O | null {
  return shown.find((o) => o.canTake) ?? null;
}

/** One option of Move to: a machine of this read and the preview's answer for it. */
export type MenuOption<T> = {
  readonly machine: MenuMachine;
  readonly target: T;
  /** The preview names no refusal for it (nor for the source). */
  readonly canTake: boolean;
  readonly cpuBefore: number | null;
  /** The refusal's sentence, or null. */
  readonly why: string | null;
};

/** What the options read of a machine: its name, id, server and state word. */
export type MenuBox = {
  readonly id: string;
  readonly display_name: string;
  readonly server: { readonly label: string | null; readonly address: string | null };
};

/**
 * The options off a preview's targets, in Move to's order, each naming a machine of this read
 * (a target the read does not hold is left out). `why` is the target's sentence or null.
 */
export function menuOptions<
  T extends { readonly runner_id: string; readonly cpu: { readonly before: number } | null },
>(
  targets: readonly T[],
  boxOf: (id: string) => MenuBox | undefined,
  stateWord: (id: string) => string,
  why: (target: T) => string | null,
): MenuOption<T>[] {
  const out: MenuOption<T>[] = [];
  for (const target of targets) {
    const box = boxOf(target.runner_id);
    if (box === undefined) continue;
    const reason = why(target);
    out.push({
      machine: {
        id: box.id,
        name: box.display_name,
        shortId: box.id.slice(0, 8),
        server: box.server.label ?? box.server.address,
        stateWord: stateWord(box.id),
      },
      target,
      canTake: reason === null,
      cpuBefore: target.cpu?.before ?? null,
      why: reason,
    });
  }
  return orderOptions(out);
}
